import { createClient } from '@supabase/supabase-js';
import {
  queueOfflineRequest as secureQueueRequest,
  getOfflineQueue,
  removeFromQueue,
  updateQueueItemStatus,
  clearCompletedFromQueue
} from './services/secureStorage';

// Supabase configuration - Environment variables required
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Validate required environment variables
if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing required environment variables: REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY');
  console.error('Please set these in your .env file or deployment environment');
  // In development, provide helpful error message
  if (process.env.NODE_ENV === 'development') {
    throw new Error('Supabase configuration missing. Check .env file.');
  }
}

// Optimized Supabase client configuration
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storageKey: 'worcester-scale-auth',
    storage: window.localStorage,
    flowType: 'pkce',
    debug: false
  },
  realtime: {
    params: {
      eventsPerSecond: 2 // Reduced from 10 for better performance
    }
  },
  db: {
    schema: 'public'
  },
  global: {
    headers: {
      'x-session-refresh': 'extended'
    }
    // Removed custom fetch that was causing issues
  }
});

// Lock to prevent double-processing of offline queue
let isProcessingQueue = false;

// Helper function for real-time subscriptions
export const subscribeToTable = (table, callback) => {
  return supabase
    .channel(`public:${table}`)
    .on('postgres_changes',
      { event: '*', schema: 'public', table: table },
      callback
    )
    .subscribe();
};

/**
 * Queue an offline request with encrypted storage
 * Wrapper around secureStorage for backwards compatibility
 */
export const queueOfflineRequest = async (request) => {
  return await secureQueueRequest(request);
};

/**
 * Process offline queue when back online
 * Uses encrypted storage with retry and status tracking
 *
 * Supports two types of queued items:
 * 1. Legacy: { table, method, data } - direct database operations
 * 2. New: { type: 'edge_function', function_name, payloads } - edge function invocations
 */
export const processOfflineQueue = async () => {
  // SECURITY: Prevent double-processing with lock
  if (isProcessingQueue) {
    console.log('Queue processing already in progress, skipping');
    return { processed: 0, reconciled: [], skipped: true };
  }

  isProcessingQueue = true;

  try {
    const queue = await getOfflineQueue();
    const pendingItems = queue.filter(item => item.status === 'pending' || item.status === 'failed');

    if (pendingItems.length === 0) {
      console.log('No pending offline requests to process');
      return { processed: 0, reconciled: [] };
    }

    console.log(`Processing ${pendingItems.length} offline requests...`);
    const reconciledCerts = [];

    for (const request of pendingItems) {
      // Skip items that have failed too many times
      if (request.retryCount >= 3) {
        console.warn(`Skipping request ${request.id} - exceeded max retries`);
        continue;
      }

      try {
        // Check request type
        if (request.type === 'edge_function') {
          // NEW: Process edge function payloads
          console.log(`Processing edge function request: ${request.function_name}`, request.meta);

          const payloads = request.payloads || [];
          const results = [];

          for (const payload of payloads) {
            // Call the edge function for each scale
            const { data, error } = await supabase.functions.invoke(request.function_name, {
              body: payload
            });

            if (error) {
              console.error(`Edge function error for scale ${payload.scale_id}:`, error);
              throw error;
            }

            if (!data?.success) {
              throw new Error(data?.error || 'Edge function failed');
            }

            results.push(data);

            // Track reconciled provisional certs
            if (payload.is_offline && data.certificate_number && !data.is_provisional_cert) {
              const originalProvisional = request.meta?.provisional_certs?.find(
                p => p.scale_id === payload.scale_id
              );

              if (originalProvisional) {
                reconciledCerts.push({
                  scale_id: payload.scale_id,
                  original_provisional: originalProvisional.provisional_cert,
                  permanent_cert: data.certificate_number,
                  reconciled_at: new Date().toISOString()
                });
                console.log(`Reconciled provisional cert: ${originalProvisional.provisional_cert} → ${data.certificate_number}`);
              }
            }
          }

          console.log(`Edge function processed ${results.length} scale(s) successfully`);

        } else {
          // LEGACY: Direct database operations
          const { error } = await supabase
            .from(request.table)
            [request.method](request.data);

          if (error) throw error;
        }

        // Mark as completed
        await updateQueueItemStatus(request.id, 'completed');
        console.log(`Successfully processed offline request: ${request.id}`);
      } catch (error) {
        console.error(`Failed to process offline request ${request.id}:`, error);
        await updateQueueItemStatus(request.id, 'failed', error.message);
      }
    }

    // Clean up completed items
    const cleared = await clearCompletedFromQueue();
    console.log(`Cleared ${cleared} completed items from queue`);

    // Return summary
    return {
      processed: pendingItems.length,
      reconciled: reconciledCerts
    };
  } finally {
    // Always release the lock
    isProcessingQueue = false;
  }
};

// Check if online and process queue
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    console.log('Back online - processing offline queue');
    processOfflineQueue();
  });

  // Also try to process on page load if online
  if (navigator.onLine) {
    // Delay to ensure storage is ready
    setTimeout(() => {
      processOfflineQueue();
    }, 2000);
  }
}

// Re-export secure storage functions for direct use if needed
export { getOfflineQueue, removeFromQueue };
